// Admin Prompt Management API Client

// @ts-ignore - Vite env
const API_BASE = import.meta.env.VITE_SUPABASE_URL || '';

export interface PromptTemplate {
    id: string;
    mode: string;
    option_key: string;
    name: string;
    prompt_template: string;
    description: string | null;
    variables: string[];
    is_active: boolean;
    generator?: string; // 'gemini' | 'nanobanana2' | 'openai'
    version?: number;
    created_at: string;
    updated_at: string;
}

export interface PromptVersion {
    id: string;
    template_id: string;
    version_number: number;
    prompt_text: string;
    change_notes: string | null;
    changed_by: string | null;
    created_at: string;
}

export interface PromptListResponse {
    success: boolean;
    prompts: PromptTemplate[];
    grouped: Record<string, PromptTemplate[]>;
}

export interface PromptDetailResponse {
    success: boolean;
    template: PromptTemplate;
    versions: PromptVersion[];
    currentVersion: number;
}

async function getAuthHeaders(): Promise<HeadersInit> {
    const { supabase } = await import('./supabase-client');
    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.access_token) {
        throw new Error('Not authenticated');
    }

    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
    };
}

async function handleResponse<T>(response: Response, errorPrefix: string): Promise<T> {
    if (!response.ok) {
        const text = await response.text();
        let errorMsg = errorPrefix;
        try {
            const errorData = JSON.parse(text);
            console.error(`Prompt Admin API Error [${response.status}]:`, errorData);
            errorMsg = errorData.error || errorData.message || errorMsg;
        } catch (e) {
            console.error(`Prompt Admin API Error [${response.status}] (Non-JSON):`, text);
            errorMsg = `${errorPrefix}: ${text.substring(0, 100)}`;
        }
        throw new Error(errorMsg);
    }
    return response.json();
}

/**
 * Fetch all prompt templates
 */
export async function fetchPrompts(): Promise<PromptListResponse> {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/prompts`, {
        method: 'GET',
        headers
    });

    return handleResponse<PromptListResponse>(response, 'Failed to fetch prompts');
}

/**
 * Fetch single prompt with version history
 */
export async function fetchPromptDetail(id: string): Promise<PromptDetailResponse> {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/prompts/${id}`, {
        method: 'GET',
        headers
    });

    return handleResponse<PromptDetailResponse>(response, 'Failed to fetch prompt');
}

/**
 * Create new prompt template for a generator
 */
export async function createPrompt(
    data: {
        mode: string;
        option_key: string;
        generator: string;
        prompt_template: string;
        name?: string;
        description?: string;
    }
): Promise<{ success: boolean; id: string }> {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/prompts`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
    });

    return handleResponse<{ success: boolean; id: string }>(response, 'Failed to create prompt');
}

/**
 * Update prompt template (creates new version)
 */
export async function updatePrompt(
    id: string,
    data: {
        prompt_template: string;
        name?: string;
        description?: string;
        variables?: string[];
        change_notes?: string;
    }
): Promise<{ success: boolean; version: number }> {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/prompts/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
    });

    return handleResponse<{ success: boolean; version: number }>(response, 'Failed to update prompt');
}

/**
 * Restore a previous version
 */
export async function restorePromptVersion(
    id: string,
    versionNumber: number
): Promise<{ success: boolean; newVersion: number }> {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/prompts/${id}/restore`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ version_number: versionNumber })
    });

    return handleResponse<{ success: boolean; newVersion: number }>(response, 'Failed to restore version');
}

/**
 * Preview prompt with variable substitution
 */
export async function previewPrompt(
    id: string,
    variables: Record<string, string>,
    promptOverride?: string
): Promise<{ success: boolean; preview: string; unsubstitutedVariables: string[] }> {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/prompts/${id}/preview`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ variables, prompt_override: promptOverride })
    });

    return handleResponse<{ success: boolean; preview: string; unsubstitutedVariables: string[] }>(response, 'Failed to preview prompt');
}

// ============================================
// Stats API
// ============================================

export interface StatsResponse {
    success: boolean;
    stats: {
        today: { total: number; byMode: Record<string, { total: number; success: number; error: number }> };
        week: { total: number; byMode: Record<string, { total: number; success: number; error: number }> };
        month: { total: number; byMode: Record<string, { total: number; success: number; error: number }> };
        insights: {
            topModes: [string, { total: number; success: number; error: number }][];
            successRateToday: number;
            successRateWeek: number;
            successRateMonth: number;
        };
    };
}

export async function fetchStats(): Promise<StatsResponse> {
    const headers = await getAuthHeaders();

    // Call Edge Function (uses SERVICE_ROLE_KEY, bypasses RLS, sees all data)
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/stats`, {
        method: 'GET',
        headers
    });

    return handleResponse<StatsResponse>(response, 'Failed to fetch stats');
}

// ============================================
// Users API
// ============================================

export interface User {
    id: string;
    email: string;
    full_name: string | null;
    available_credits: number;
    credits_type: string;
    subscription_status: string;
    created_at: string;
    scheduled_deletion_at?: string | null;
}

export interface UsersResponse {
    success: boolean;
    users: User[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}

export async function fetchUsers(page = 1, limit = 50): Promise<UsersResponse> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/users?page=${page}&limit=${limit}`, {
        method: 'GET',
        headers
    });
    return handleResponse<UsersResponse>(response, 'Failed to fetch users');
}

export async function updateUserCredits(
    userId: string,
    credits: number,
    operation: 'set' | 'add' | 'subtract'
): Promise<{ success: boolean; previousCredits: number; newCredits: number }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/users/${userId}/credits`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ credits, operation })
    });
    return handleResponse<{ success: boolean; previousCredits: number; newCredits: number }>(response, 'Failed to update credits');
}

// ============================================
// User Deletion API
// ============================================

export interface ScheduledDeletion {
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    scheduled_deletion_at: string;
    deletion_requested_at: string;
    deletion_requested_by: string;
}

export async function deleteUser(
    userId: string,
    immediate = false
): Promise<{ success: boolean; message: string; deletionType: string; scheduledFor?: string }> {
    const headers = await getAuthHeaders();
    const url = immediate
        ? `${API_BASE}/functions/v1/prompt-admin/users/${userId}?immediate=true`
        : `${API_BASE}/functions/v1/prompt-admin/users/${userId}`;

    const response = await fetch(url, {
        method: 'DELETE',
        headers
    });
    return handleResponse(response, 'Failed to delete user');
}

export async function fetchScheduledDeletions(): Promise<{ scheduledDeletions: ScheduledDeletion[]; count: number }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/users/scheduled-deletions`, {
        headers
    });
    return handleResponse(response, 'Failed to fetch scheduled deletions');
}

export async function cancelUserDeletion(
    userId: string
): Promise<{ success: boolean; message: string; email: string }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/users/${userId}/cancel-deletion`, {
        method: 'POST',
        headers
    });
    return handleResponse(response, 'Failed to cancel deletion');
}

// ============================================
// Logs API
// ============================================

export interface OutputVariant {
    url: string;
    provider: string;
    prompt_used: string | null;
    processing_time_ms: number | null;
}

export interface GenerationLog {
    id: string;
    user_id: string;
    mode: string;
    option_key: string | null;
    status: string;
    error_message: string | null;
    input_image_url: string | null;
    output_variants: OutputVariant[];
    room_type: string | null;
    style: string | null;
    target_group: string | null;
    credits_used: number;
    created_at: string;
    completed_at: string | null;
    profiles: {
        email: string;
        first_name: string | null;
        last_name: string | null;
    } | null;
}

export interface LogsResponse {
    success: boolean;
    logs: GenerationLog[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}

export async function fetchLogs(
    page = 1,
    limit = 50,
    filters?: { mode?: string; status?: string; userId?: string }
): Promise<LogsResponse> {
    const headers = await getAuthHeaders();

    // Build query params
    const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
    });
    if (filters?.mode) params.append('mode', filters.mode);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.userId) params.append('user_id', filters.userId);

    // Call Edge Function (uses SERVICE_ROLE_KEY, bypasses RLS)
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/logs?${params}`, {
        method: 'GET',
        headers
    });

    const data = await handleResponse<{
        success: boolean;
        logs: Array<{
            id: string;
            user_id: string;
            mode: string;
            option_key: string | null;
            status: string;
            error_message: string | null;
            input_image_gcs: { path: string; bucket: string } | null;
            output_variants: Array<{
                gcs_path: string;
                bucket: string;
                provider: string;
                prompt_used: string | null;
                processing_time_ms: number | null;
            }>;
            credits_used: number;
            created_at: string;
            completed_at: string | null;
            room_type: string | null;
            style: string | null;
            target_group: string | null;
            profiles: {
                email: string;
                first_name: string | null;
                last_name: string | null;
            } | null;
        }>;
        pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
        };
    }>(response, 'Failed to fetch logs');

    // Sign input image URLs (gcs_path -> signed URL)
    const { batchSignUrls } = await import('./gcs-client');
    const pathsToSign: { bucket: string; path: string }[] = [];

    data.logs.forEach((log) => {
        if (log.input_image_gcs?.path) {
            pathsToSign.push({
                bucket: log.input_image_gcs.bucket,
                path: log.input_image_gcs.path
            });
        }
    });

    // Collect output variant GCS paths for signing
    data.logs.forEach((log) => {
        if (log.output_variants && Array.isArray(log.output_variants)) {
            log.output_variants.forEach((variant) => {
                if (variant.gcs_path) {
                    pathsToSign.push({
                        bucket: variant.bucket,
                        path: variant.gcs_path
                    });
                }
            });
        }
    });

    let signedUrlsMap: Record<string, string> = {};
    if (pathsToSign.length > 0) {
        try {
            signedUrlsMap = await batchSignUrls(pathsToSign);
        } catch (e) {
            console.warn('Failed to sign input image URLs:', e);
        }
    }

    // Map to GenerationLog format with signed URLs
    const logs: GenerationLog[] = data.logs.map((log) => ({
        id: log.id,
        user_id: log.user_id,
        mode: log.mode,
        option_key: log.option_key,
        status: log.status,
        error_message: log.error_message,
        input_image_url: log.input_image_gcs?.path
            ? (signedUrlsMap[log.input_image_gcs.path] || null)
            : null,
        // Map output variants with signed URLs
        output_variants: (log.output_variants || [])
            .map((variant) => ({
                url: signedUrlsMap[variant.gcs_path] || '',
                provider: variant.provider || 'unknown',
                prompt_used: variant.prompt_used,
                processing_time_ms: variant.processing_time_ms
            }))
            .filter((v): v is OutputVariant => !!v.url),
        room_type: log.room_type,
        style: log.style,
        target_group: log.target_group,
        credits_used: log.credits_used,
        created_at: log.created_at,
        completed_at: log.completed_at,
        profiles: log.profiles
    }));

    return {
        success: true,
        logs,
        pagination: data.pagination
    };
}

// Mode display names
export const MODE_LABELS: Record<string, string> = {
    'enhance': 'Poprawa Jakości',
    'staging': 'Wirtualne Umeblowanie',
    'renovation': 'Renowacja'
};

// Mode icons (lucide icon names)
export const MODE_ICONS: Record<string, string> = {
    'enhance': 'WandSparkles',
    'staging': 'Sofa',
    'renovation': 'Hammer'
};

// Generator labels
export const GENERATOR_LABELS: Record<string, string> = {
    'gemini': 'Nano Banana Pro',
    'nanobanana2': 'NanoBanana2 (Flash)',
    'openai': 'GPT Image 1.5',
    'gpt-image-2': 'GPT Image 2',
    'flux-2-pro': 'FLUX.2 Pro',
    'flux-2-pro-preview': 'FLUX.2 Pro Preview',
    'flux-2-max': 'FLUX.2 Max',
    'flux-2-flex': 'FLUX.2 Flex',
    'flux-2-klein-9b': 'FLUX.2 Klein 9B',
    'flux-2-klein-4b': 'FLUX.2 Klein 4B',
    'flux-kontext-pro': 'FLUX Kontext Pro',
    'flux-kontext-max': 'FLUX Kontext Max',
};

// Generator registry entry (from backend)
export interface GeneratorRegistryEntry {
    label: string;
    color: string;
}

export interface ActiveGeneratorsConfig {
    variant_1: string;
    variant_2: string;
}

export interface GeneratorConfigResponse {
    success: boolean;
    registry: Record<string, GeneratorRegistryEntry>;
    active: ActiveGeneratorsConfig;
}

/**
 * Fetch generator registry + active config
 */
export async function fetchGeneratorConfig(): Promise<GeneratorConfigResponse> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/generators`, {
        method: 'GET',
        headers,
    });
    return handleResponse<GeneratorConfigResponse>(response, 'Failed to fetch generator config');
}

/**
 * Update active generators
 */
export async function updateActiveGenerators(
    config: ActiveGeneratorsConfig
): Promise<{ success: boolean }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/generators`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(config),
    });
    return handleResponse<{ success: boolean }>(response, 'Failed to update generators');
}

// ============================================
// ENHANCE TOGGLES API
// ============================================

// Types imported from shared definitions (S6-009)
import type { EnhanceToggle, ModeOption, InteriorStyle } from '../types/render-options';
export type { EnhanceToggle, ModeOption, InteriorStyle };

export async function fetchEnhanceToggles(): Promise<{ success: boolean; toggles: EnhanceToggle[] }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/enhance-toggles`, {
        method: 'GET',
        headers
    });
    return handleResponse(response, 'Failed to fetch enhance toggles');
}

export async function createEnhanceToggle(data: Partial<EnhanceToggle>): Promise<{ success: boolean; toggle: EnhanceToggle }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/enhance-toggles`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
    });
    return handleResponse(response, 'Failed to create enhance toggle');
}

export async function updateEnhanceToggle(id: string, data: Partial<EnhanceToggle>): Promise<{ success: boolean }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/enhance-toggles/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
    });
    return handleResponse(response, 'Failed to update enhance toggle');
}

export async function deleteEnhanceToggle(id: string): Promise<{ success: boolean }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/enhance-toggles/${id}`, {
        method: 'DELETE',
        headers
    });
    return handleResponse(response, 'Failed to delete enhance toggle');
}

// ============================================
// MODE OPTIONS API (staging personas, renovation styles)
// ============================================

export async function fetchModeOptions(mode: 'staging' | 'renovation'): Promise<{ success: boolean; options: ModeOption[] }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/mode-options/${mode}`, {
        method: 'GET',
        headers
    });
    return handleResponse(response, 'Failed to fetch mode options');
}

export async function createModeOption(data: Partial<ModeOption>): Promise<{ success: boolean; option: ModeOption }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/mode-options`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
    });
    return handleResponse(response, 'Failed to create mode option');
}

export async function updateModeOption(id: string, data: Partial<ModeOption>): Promise<{ success: boolean }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/mode-options/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
    });
    return handleResponse(response, 'Failed to update mode option');
}

export async function deleteModeOption(id: string): Promise<{ success: boolean }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/mode-options/${id}`, {
        method: 'DELETE',
        headers
    });
    return handleResponse(response, 'Failed to delete mode option');
}

// ============================================
// INTERIOR STYLES API
// ============================================

export async function fetchInteriorStyles(): Promise<{ success: boolean; styles: InteriorStyle[] }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/interior-styles`, {
        method: 'GET',
        headers
    });
    return handleResponse(response, 'Failed to fetch interior styles');
}

export async function createInteriorStyle(data: Partial<InteriorStyle>): Promise<{ success: boolean; style: InteriorStyle }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/interior-styles`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
    });
    return handleResponse(response, 'Failed to create interior style');
}

export async function updateInteriorStyle(id: string, data: Partial<InteriorStyle>): Promise<{ success: boolean }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/interior-styles/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
    });
    return handleResponse(response, 'Failed to update interior style');
}

export async function deleteInteriorStyle(id: string): Promise<{ success: boolean }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/interior-styles/${id}`, {
        method: 'DELETE',
        headers
    });
    return handleResponse(response, 'Failed to delete interior style');
}

// ============================================
// MAINTENANCE MODE API
// ============================================

export interface MaintenanceStatus {
    enabled: boolean;
    message: string;
    started_at: string | null;
}

/**
 * Check maintenance status (PUBLIC - no auth required)
 */
export async function checkMaintenanceStatus(): Promise<MaintenanceStatus> {
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/maintenance`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
        // Default to not in maintenance on error
        return { enabled: false, message: '', started_at: null };
    }

    return response.json();
}

/**
 * Toggle maintenance mode (ADMIN ONLY)
 */
export async function setMaintenanceMode(
    enabled: boolean,
    message?: string
): Promise<{ success: boolean } & MaintenanceStatus> {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/maintenance`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ enabled, message })
    });

    return handleResponse(response, 'Failed to update maintenance mode');
}

// ============================================
// REGISTRATION CHECKBOXES API
// ============================================

export interface RegistrationCheckboxes {
    tos_text: string;
    tos_url: string;
    tos_link_label: string;
    newsletter_text: string;
}

/**
 * Fetch registration checkbox texts (PUBLIC - no auth required)
 */
export async function fetchRegistrationCheckboxes(): Promise<RegistrationCheckboxes> {
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/registration-checkboxes`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
        return {
            tos_text: "Akceptuję {regulamin}",
            tos_url: "https://www.wysko.pl/regulamin",
            tos_link_label: "Regulamin",
            newsletter_text: "Chcę otrzymywać newsletter z poradami i nowościami"
        };
    }

    return response.json();
}

/**
 * Update registration checkbox texts (ADMIN ONLY)
 */
export async function updateRegistrationCheckboxes(
    data: RegistrationCheckboxes
): Promise<{ success: boolean } & RegistrationCheckboxes> {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/registration-checkboxes`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
    });

    return handleResponse(response, 'Failed to update registration checkboxes');
}

// ============================================
// Email timing, stats & logs
// ============================================

export interface EmailTimingConfig {
    email_type: string;
    min_delay_hours: number;
    max_delay_hours: number;
    enabled: boolean;
    updated_at: string;
    updated_by: string | null;
}

export interface EmailStats {
    type: string;
    total: number;
    successful: number;
    failed: number;
}

export interface EmailLogEntry {
    id: string;
    user_id: string;
    email_type: string;
    resend_message_id: string | null;
    template_id: string | null;
    sent_at: string;
    status: string;
    error_message: string | null;
    profiles?: { email: string; first_name: string | null } | null;
}

export interface EmailLogsResponse {
    success: boolean;
    data: EmailLogEntry[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}

/**
 * Fetch email timing configuration (ADMIN ONLY)
 */
export async function fetchEmailTimingConfig(): Promise<{ success: boolean; data: EmailTimingConfig[] }> {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/email-timing`, {
        method: 'GET',
        headers
    });

    return handleResponse(response, 'Failed to fetch email timing config');
}

/**
 * Update email timing for a specific email type (ADMIN ONLY)
 */
export async function updateEmailTimingConfig(
    emailType: string,
    data: { min_delay_hours?: number; max_delay_hours?: number; enabled?: boolean }
): Promise<{ success: boolean; data: EmailTimingConfig }> {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/email-timing/${emailType}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
    });

    return handleResponse(response, 'Failed to update email timing');
}

/**
 * Fetch email send statistics (ADMIN ONLY)
 */
export async function fetchEmailStats(): Promise<{ success: boolean; stats: EmailStats[] }> {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/email-stats`, {
        method: 'GET',
        headers
    });

    return handleResponse(response, 'Failed to fetch email stats');
}

/**
 * Fetch paginated email logs (ADMIN ONLY)
 */
export async function fetchEmailLogs(page = 1, limit = 20): Promise<EmailLogsResponse> {
    const headers = await getAuthHeaders();

    const params = new URLSearchParams({ page: String(page), limit: String(limit) });

    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/email-logs?${params}`, {
        method: 'GET',
        headers
    });

    return handleResponse(response, 'Failed to fetch email logs');
}

// ============================================
// Plans API
// ============================================

export interface Plan {
    id: string;
    name: string;
    price: number;
    credits: number;
    features: string[];
    checkout_url: string;
    variant_id: string;
    is_active: boolean;
}

export async function fetchPlans(): Promise<{ success: boolean; plans: Plan[] }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/plans`, {
        method: 'GET',
        headers
    });
    return handleResponse(response, 'Failed to fetch plans');
}

export async function updatePlan(
    id: string,
    data: { name: string; price: number; credits: number; features: string[]; checkout_url: string; variant_id: string }
): Promise<{ success: boolean }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/functions/v1/prompt-admin/plans/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
    });
    return handleResponse(response, 'Failed to update plan');
}
